import { useToken } from "@gluestack-style/react";
import Breadcrumbs from "components/general/Breadcrumbs";
import { CallToAction } from "components/general/CallToAction";
import CardTag from "components/general/CardTag";
import Container from "components/general/Container";
import ScrollContainer from "components/general/ScrollContainer";
import Section from "components/general/Section";
import Seo from "components/general/Seo";
import SocialShare from "components/general/SocialShare";
import ItemCardList from "components/item/ItemCardList";
import { PageRender } from "components/page/PageRender";
import { Box, H1, HStack, Typography, VStack } from "components/ui";
import { Calendar, Clock } from "lucide-react-native";
import React, { useState } from "react";
import { Platform, ViewStyle } from "react-native";
import Animated, { Easing, useAnimatedStyle, useSharedValue, withRepeat, withTiming } from "react-native-reanimated";
import { PageRead } from "store/abcoApi";
import { formatDate } from "utils/globalFunctions";
import { useIsMobile } from "utils/hooks/useIsMobile";

const articleTypeMap: { [key: number]: string } = {
	1: "News",
	2: "Guide",
	3: "Case Study",
	4: "Innovation",
};

export const ImageSkeleton = () => {
	const pulseValue = useSharedValue(0);

	React.useEffect(() => {
		pulseValue.value = withRepeat(
			withTiming(1, { duration: 1200, easing: Easing.inOut(Easing.ease) }),
			-1,
			true, // yo-yo effect
		);
	}, []);

	const pulseStyle = useAnimatedStyle(() => {
		return {
			position: "absolute",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			backgroundColor: "#e0e0e0",
			opacity: 0.3 + pulseValue.value * 0.4, // Subtle but visible pulsing
			borderRadius: 20,
		};
	});

	const containerStyle: ViewStyle = {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "#e8e8e8", // Slightly lighter base color
		borderRadius: 20,
		overflow: "hidden",
	};

	return (
		<Box style={containerStyle}>
			<Animated.View style={pulseStyle} />
		</Box>
	);
};
export default function PageDetailArticle({ page }: { page: PageRead }) {
	const resolvedPrimarylight = useToken("colors", "primary");
	const resolvedSecondary = useToken("colors", "secondary");
	const isMobile = useIsMobile();
	const placeholderImage = page.featureImageUrl;
	const [imageLoaded, setImageLoaded] = useState(false);
	const defaultTextProps = {
		selectable: true,
	};
	const tag = articleTypeMap[page.articleType as number] || "";

	return (
		<ScrollContainer>
			<Seo title={page.metaTitle} description={page.metaDescription} />

			{/* <Box bg="$neutral25"> */}
			{/* <Container> */}
			<Container>
				<Section bg="transparent" gap={24}>
					<Breadcrumbs />

					<H1>{page.pageName}</H1>
					<HStack gap={24}>
						<Box gap={8} flexDirection="row" alignItems="center">
							<Calendar color={resolvedPrimarylight} />
							<Typography weight="bold" sx={{ color: "$neutral600" }}>
								{formatDate(page.documentDate || "")}
							</Typography>
						</Box>
						<Box gap={8} flexDirection="row" alignItems="center">
							<Clock color={resolvedPrimarylight} />
							<Typography weight="bold" sx={{ color: "$neutral600" }}>
								5min read time
							</Typography>
						</Box>
					</HStack>
					<Box flexDirection="row" justifyContent="space-between" alignItems="center">
						<HStack space="sm" flexWrap="wrap">
							<CardTag variant="primary" tag={tag} />
						</HStack>
						<HStack gap={8}>
							<Box alignItems="center" justifyContent="center">
								<Typography weight="bold">Share:</Typography>
							</Box>
							<SocialShare
								title={page.pageName || "Check this out!"}
								url={Platform.OS === "web" ? window.location.href : process.env.EXPO_PUBLIC_ABCO_URL || "https://example.com"}
								message={`Check out this article: ${page.pageName || ""}`}
							/>
						</HStack>
					</Box>
				</Section>
			</Container>
			<Section>
				<Container sx={{ alignItems: "center" }}>
					<Box paddingHorizontal={16} maxWidth={1024}>
						{page.content ? <PageRender content={page.content} /> : <Typography>No content available for this article.</Typography>}
					</Box>
				</Container>
			</Section>
			{page.relatedItemCollection && page.relatedItemCollection.length > 0 && (
				<Box bg="$neutral25">
					<Container>
						<Section>
							<VStack gap={16}>
								<Typography.H3>Related Items</Typography.H3>
								<ItemCardList list={page.relatedItemCollection} isGridView={page.relatedItemCollection.length > 1 ? true : false} />
							</VStack>
						</Section>
					</Container>
				</Box>
			)}
			{page.callToAction && <CallToAction type={page.callToAction as "Contact Us" | "Download Brochure" | "Request a Quote" | "Book a Meeting"} />}
			{/* <H1>{page.callToAction}</H1> */}

			{/* </Container> */}
			{/* </Box> */}
		</ScrollContainer>
	);
}
